import { BackdropLoading } from '@/components/backdrop-loading'
import { ModalActions, ModalContainer } from '@/components/container/container'
import { Client, getClientsByOrganization } from '@/lib/api/clients/client-api'
import {
    createWaitingListEntry,
    PRIORITY_LABELS,
    updateWaitingListEntry,
    WaitingListEntry,
} from '@/lib/api/waiting-list/waiting-list-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useAuth } from '@/store/hooks'
import React, { useEffect, useState } from 'react'
import { MdClose, MdHourglassEmpty, MdPerson } from 'react-icons/md'
import { toast } from 'react-toastify'

interface WaitingListModalProps {
    agendaId: number
    entry?: WaitingListEntry | null
    onClose: () => void
    onSuccess: () => void
}

export function WaitingListModal({
    agendaId,
    entry,
    onClose,
    onSuccess,
}: WaitingListModalProps) {
    const { user } = useAuth()
    const { clientLabel } = useClientLabel()
    const isEdit = !!entry

    const [loading, setLoading] = useState(false)
    const [clientId, setClientId] = useState<number | null>(null)
    const [clientSearch, setClientSearch] = useState('')
    const [clients, setClients] = useState<Client[]>([])
    const [showClientSearch, setShowClientSearch] = useState(false)
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const [searchingClients, setSearchingClients] = useState(false)
    const [priority, setPriority] = useState(5)
    const [preferredDates, setPreferredDates] = useState('')
    const [preferredTimes, setPreferredTimes] = useState('')
    const [notes, setNotes] = useState('')

    useEffect(() => {
        if (entry) {
            setClientId(entry.clientId)
            if (entry.client) {
                setSelectedClient({
                    id: entry.client.id,
                    name: entry.client.name,
                } as Client)
            }
            setPriority(entry.priority)
            setPreferredDates(entry.preferredDates || '')
            setPreferredTimes(entry.preferredTimes || '')
            setNotes(entry.notes || '')
        }
    }, [entry])

    useEffect(() => {
        if (clientSearch.trim() && user?.companyId) {
            const timer = setTimeout(() => {
                searchClientFn()
            }, 300)

            return () => clearTimeout(timer)
        } else {
            setClients([])
        }
    }, [clientSearch, user?.companyId])

    const searchClientFn = async () => {
        if (!user?.companyId || !clientSearch.trim()) return

        setSearchingClients(true)
        try {
            const response = await getClientsByOrganization(user.organizationId, {
                page: 1,
                limit: 50,
                search: clientSearch,
            })
            if (response.success) {
                setClients(response.result)
            }
        } catch (error) {
            console.error('Erro ao buscar clientes:', error)
        } finally {
            setSearchingClients(false)
        }
    }

    const handleSubmit = async () => {
        if (!clientId && !isEdit) {
            return toast.error(`Selecione um ${clientLabel}`)
        }

        setLoading(true)
        try {
            const data = {
                agendaId,
                clientId: clientId!,
                priority,
                preferredDates: preferredDates.trim() || undefined,
                preferredTimes: preferredTimes.trim() || undefined,
                notes: notes.trim() || undefined,
            }

            const response = isEdit
                ? await updateWaitingListEntry(entry!.id, data)
                : await createWaitingListEntry(data)

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Entrada atualizada com sucesso'
                        : 'Adicionado a lista de espera'
                )
                onSuccess()
                onClose()
            } else {
                toast.error(response.error || 'Erro ao salvar')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao salvar')
        } finally {
            setLoading(false)
        }
    }

    return (
        <React.Fragment>
            <dialog className="modal modal-open">
                <ModalContainer size="sm" className="!overflow-visible">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100">
                            <MdHourglassEmpty className="text-xl text-amber-800" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">
                                {isEdit
                                    ? 'Editar na Lista de Espera'
                                    : 'Adicionar a Lista de Espera'}
                            </h3>
                            <p className="text-sm text-base-content/60">
                                Gerencie a fila de agendamentos
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {!isEdit && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        {clientLabel} *
                                    </span>
                                </label>
                                <div className="relative client-search-container">
                                    {selectedClient ? (
                                        <div className="flex items-center justify-between p-3 border border-base-300 rounded-lg bg-base-100">
                                            <div className="flex items-center gap-2">
                                                <MdPerson className="text-base-content/60" />
                                                <span className="font-medium">
                                                    {selectedClient.name}
                                                </span>
                                            </div>
                                            <button
                                                className="btn btn-xs btn-ghost"
                                                onClick={() => {
                                                    setSelectedClient(null)
                                                    setClientId(null)
                                                    setClientSearch('')
                                                }}
                                            >
                                                <MdClose />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder={`Buscar ${clientLabel}`}
                                                className="input input-bordered w-full"
                                                value={clientSearch}
                                                onChange={e => {
                                                    setClientSearch(
                                                        e.target.value
                                                    )
                                                    setShowClientSearch(true)
                                                }}
                                                onFocus={() =>
                                                    setShowClientSearch(true)
                                                }
                                            />
                                            {showClientSearch &&
                                                (clients.length > 0 ||
                                                    searchingClients) && (
                                                    <div className="absolute z-50 w-full mt-2 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                        {searchingClients ? (
                                                            <div className="p-4 text-center text-sm text-base-content/60">
                                                                Buscando...
                                                            </div>
                                                        ) : (
                                                            clients.map(
                                                                client => (
                                                                    <button
                                                                        key={
                                                                            client.id
                                                                        }
                                                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300 last:border-b-0"
                                                                        onClick={() => {
                                                                            setSelectedClient(
                                                                                client
                                                                            )
                                                                            setClientId(
                                                                                client.id
                                                                            )
                                                                            setClientSearch(
                                                                                ''
                                                                            )
                                                                            setShowClientSearch(
                                                                                false
                                                                            )
                                                                        }}
                                                                    >
                                                                        <div className="font-medium">
                                                                            {
                                                                                client.name
                                                                            }
                                                                        </div>
                                                                        {client.email && (
                                                                            <div className="text-xs text-base-content/60">
                                                                                {
                                                                                    client.email
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </button>
                                                                )
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {isEdit && selectedClient && (
                            <div className="flex items-center gap-2 p-3 bg-base-200 rounded-lg">
                                <MdPerson className="text-base-content/60" />
                                <span className="font-medium">
                                    {selectedClient.name}
                                </span>
                            </div>
                        )}

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Prioridade
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={priority}
                                onChange={e =>
                                    setPriority(parseInt(e.target.value))
                                }
                            >
                                {PRIORITY_LABELS.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label} ({option.value})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Datas Preferidas
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Segunda e quarta, ou 15/01, 16/01"
                                value={preferredDates}
                                onChange={e => setPreferredDates(e.target.value)}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Horários Preferidos
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Manhã, ou 08:00-12:00"
                                value={preferredTimes}
                                onChange={e => setPreferredTimes(e.target.value)}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Informações adicionais"
                                rows={2}
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <ModalActions>
                        <button className="btn btn-ghost" onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading
                                ? 'Salvando...'
                                : isEdit
                                    ? 'Atualizar'
                                    : 'Adicionar'}
                        </button>
                    </ModalActions>
                </ModalContainer>
            </dialog>

            <BackdropLoading visible={loading} />
        </React.Fragment>
    )
}
